/** localStorage draft for second “why previous brand” when Q2 = Yes. */
export const OTHER_PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:otherPrevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export type OtherPrevBrandAnswerAnswers = {
  taste: ElaboratedReason;
  price: ElaboratedReason;
  productFeatures: ElaboratedReason;
  brandCredibility: ElaboratedReason;
  peersSmoking: ElaboratedReason;
  otherReason: string;
  /** How many of 10 smokes are the first (main) product; second = 10 - this. */
  firstProductCount: number;
  /** True once the respondent has moved the smoke-frequency slider. */
  smokeFrequencyAdjusted: boolean;
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyOtherPrevBrandAnswerAnswers = (): OtherPrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  brandCredibility: emptyElaborated(),
  peersSmoking: emptyElaborated(),
  otherReason: '',
  firstProductCount: 5,
  smokeFrequencyAdjusted: false,
});

function parseFirstProductCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 10) {
    return 5;
  }
  return value;
}

function parseElaborated(value: unknown): ElaboratedReason {
  // Back-compat: older drafts stored brandCredibility / peersSmoking as booleans.
  if (value === true) return { checked: true, elaboration: '' };
  if (value === false || value == null) return emptyElaborated();
  if (typeof value !== 'object') return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readOtherPrevBrandAnswerAnswers(): OtherPrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyOtherPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyOtherPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    return {
      taste: parseElaborated(o.taste),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      brandCredibility: parseElaborated(o.brandCredibility),
      peersSmoking: parseElaborated(o.peersSmoking),
      otherReason: typeof o.otherReason === 'string' ? o.otherReason : '',
      firstProductCount: parseFirstProductCount(o.firstProductCount),
      smokeFrequencyAdjusted: o.smokeFrequencyAdjusted === true,
    };
  } catch {
    return emptyOtherPrevBrandAnswerAnswers();
  }
}

export function writeOtherPrevBrandAnswerAnswers(data: OtherPrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearOtherPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type OtherPrevBrandReasonType =
  | 'taste'
  | 'price'
  | 'productFeatures'
  | 'brandCredibility'
  | 'peersSmoking'
  | 'other';

export type OtherPrevBrandReasonSelection = {
  type: OtherPrevBrandReasonType;
  elaboration: string;
};

const ELABORATED_KEYS = [
  'taste',
  'price',
  'productFeatures',
  'brandCredibility',
  'peersSmoking',
] as const;

function hasAnyReasonSelected(data: OtherPrevBrandAnswerAnswers): boolean {
  return (
    ELABORATED_KEYS.some((key) => data[key].checked) || data.otherReason.trim().length > 0
  );
}

function elaboratedReasonComplete(data: OtherPrevBrandAnswerAnswers): boolean {
  return ELABORATED_KEYS.every(
    (key) => !data[key].checked || data[key].elaboration.trim().length > 0
  );
}

export function isOtherPrevBrandAnswerComplete(): boolean {
  const data = readOtherPrevBrandAnswerAnswers();
  if (!hasAnyReasonSelected(data)) return false;
  if (!elaboratedReasonComplete(data)) return false;
  if (!data.smokeFrequencyAdjusted) return false;
  return Number.isInteger(data.firstProductCount) && data.firstProductCount >= 0 && data.firstProductCount <= 10;
}

export function getSelectedOtherPrevBrandReasons(
  data: OtherPrevBrandAnswerAnswers
): OtherPrevBrandReasonSelection[] {
  const selected: OtherPrevBrandReasonSelection[] = [];
  for (const key of ELABORATED_KEYS) {
    if (data[key].checked) {
      selected.push({ type: key, elaboration: data[key].elaboration });
    }
  }
  const other = data.otherReason.trim();
  if (other.length > 0) {
    selected.push({ type: 'other', elaboration: other });
  }
  return selected;
}
